import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    merge,
    Observable,
    of,
    share,
    startWith,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';

export interface DropdownOption {
    id: number | string;
    name: string;
    [key: string]: any;
}
/**
 * >> Basic usage with static options:
    <app-unified-style-dropdown  
    [options]="myOptions" 
    label="Select Option" 
    searchPlaceholder="Search options..." 
    [formControl]="selectedOption"
     ></app-unified-style-dropdown>

  >> Multiple selection:
     <app-unified-style-dropdown  
     [options]="myOptions"  
     [multiple]="true"  
     label="Select Options"  
     [formControl]="selectedOptions">
     </app-unified-style-dropdown>

  >> With async search:
     <app-unified-style-dropdown
     [searchFn]="mySearchFunction"
     label="Search and Select" 
     [formControl]="selectedItem">
     </app-unified-style-dropdown>

 */
@Component({
    selector: 'app-unified-style-dropdown',
    templateUrl: './unified-style-dropdown.component.html',
    styleUrls: ['./unified-style-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UnifiedStyleDropdownComponent),
            multi: true,
        },
    ],
})
export class UnifiedStyleDropdownComponent
    extends OnDestroyHandler
    implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy
{
    @ViewChild(MatSelect) matSelect: MatSelect;

    // Inputs
    @Input() label: string = '';
    @Input() searchPlaceholder: string = 'Search...';
    @Input() options: DropdownOption[] = [];
    @Input() multiple: boolean = false;
    @Input() disabled: boolean = false;
    @Input() displayKey: string = 'name';
    @Input() valueKey: string = 'id';
    @Input() searchFn?: (term: string) => Observable<DropdownOption[]>;
    @Input() loadOnInit: boolean = false;
    @Input() debounceTime: number = 300;
    @Input() minSearchLength: number = 0;
    @Input() displayMode: 'chip' | 'dropdown' = 'chip';

    // Outputs
    @Output() optionSelected: EventEmitter<DropdownOption> = new EventEmitter<DropdownOption>();
    @Output() optionRemoved: EventEmitter<DropdownOption> = new EventEmitter<DropdownOption>();
    @Output() searchChange: EventEmitter<string> = new EventEmitter<string>();

    // Internal state
    public searchControl = new FormControl('');
    public selectControl = new FormControl();
    public selectedOptions: DropdownOption[] = [];
    public filteredOptions$: Observable<DropdownOption[]>;
    private refresh: Subject<string> = new Subject();
    private _value: any;

    // ControlValueAccessor callbacks
    private propagateChange = (_: any) => {};
    private propagateTouched = () => {};

    constructor(protected cdr: ChangeDetectorRef) {
        super();
    }

    ngAfterViewInit(): void {
        this.setupSearchObservable();
        if (this.loadOnInit && !this.searchFn) {
            this.refresh.next('');
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.options && !this.searchFn) {
            this.refresh.next('');
        }
        if (changes.disabled) {
            this.setDisabledState(this.disabled);
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.refresh.complete();
    }

    private setupSearchObservable(): void {
        // For mat-select, we filter options based on search control or show all options
        this.filteredOptions$ = merge(
            this.searchControl.valueChanges.pipe(
                startWith(''),
                debounceTime(this.debounceTime),
                distinctUntilChanged()
            ),
            this.refresh
        ).pipe(
            filter((term: string) => {
                if (this.searchFn) {
                    return typeof term === 'string' && term.length >= this.minSearchLength;
                }
                return typeof term === 'string';
            }),
            share(),
            switchMap((term: string) => {
                const searchString = term || '';
                if (this.searchFn) {
                    this.searchChange.emit(searchString);
                    return this.searchFn(searchString);
                } else {
                    return this.filterStaticOptions(searchString);
                }
            }),
            takeUntil(this.destroy)
        );
    }

    private filterStaticOptions(term: string): Observable<DropdownOption[]> {
        const searchTerm = (term || '').toLowerCase().trim();
        if (!searchTerm) {
            return of(this.options || []);
        }

        const filtered = (this.options || []).filter((option) => {
            const displayValue = this.getDisplayValue(option);
            return displayValue.toLowerCase().includes(searchTerm);
        });

        return of(filtered);
    }

    public getDisplayValue(option: DropdownOption): string {
        if (!option) {
            return '';
        }
        return option[this.displayKey] || option.name || String(option[this.valueKey]);
    }

    public displayWithFn = (option: DropdownOption): string => {
        return this.getDisplayValue(option);
    };

    public getValue(option: DropdownOption): any {
        if (!option) {
            return null;
        }
        return option[this.valueKey] || option.id;
    }

    public onSelectionChange(event: any): void {
        const selectedValue = event.value;

        if (this.multiple) {
            // For multiple selection, selectedValue is an array
            this.selectedOptions = Array.isArray(selectedValue) ? selectedValue : [];
        } else {
            // For single selection, selectedValue is the option object
            this.selectedOptions = selectedValue ? [selectedValue] : [];
        }

        this.updateValue();

        // Emit events for each selected option
        if (this.multiple) {
            this.selectedOptions.forEach((option) => {
                if (!this.selectedOptions.some((opt) => this.getValue(opt) === this.getValue(option))) {
                    this.optionSelected.emit(option);
                }
            });
        } else {
            if (selectedValue) {
                this.optionSelected.emit(selectedValue);
            }
        }

        this.cdr.markForCheck();
    }

    public onRemoveOption(option: DropdownOption): void {
        const index = this.selectedOptions.findIndex((opt) => this.getValue(opt) === this.getValue(option));
        if (index !== -1) {
            const removed = this.selectedOptions.splice(index, 1)[0];

            // Update select control
            if (this.multiple) {
                this.selectControl.setValue(this.selectedOptions, { emitEvent: false });
            } else {
                this.selectControl.setValue(null, { emitEvent: false });
            }

            this.updateValue();
            this.optionRemoved.emit(removed);
            this.cdr.markForCheck();
        }
    }

    public clearSelection(): void {
        this.selectedOptions = [];
        this.selectControl.setValue(this.multiple ? [] : null, { emitEvent: false });
        this.updateValue();
        this.searchControl.setValue('', { emitEvent: false });
        this.cdr.markForCheck();
    }

    private updateValue(): void {
        if (this.multiple) {
            const values = this.selectedOptions.map((opt) => this.getValue(opt));
            this._value = values;
        } else {
            this._value = this.selectedOptions.length > 0 ? this.getValue(this.selectedOptions[0]) : null;
        }
        this.propagateChange(this._value);
    }

    // ControlValueAccessor implementation
    public writeValue(value: any): void {
        this._value = value;

        if (value === null || value === undefined) {
            this.selectedOptions = [];
            this.selectControl.setValue(this.multiple ? [] : null, { emitEvent: false });
            this.cdr.markForCheck();
            return;
        }

        const valuesToFind = Array.isArray(value) ? value : [value];
        this.selectedOptions = [];

        if (this.searchFn) {
            // For async search, we can't easily resolve by ID
            // User should handle this through options input or custom searchFn
            if (this.options && this.options.length > 0) {
                this.resolveOptionsFromValue(valuesToFind);
            }
        } else {
            this.resolveOptionsFromValue(valuesToFind);
        }

        // Update the select control with the resolved options
        if (this.multiple) {
            this.selectControl.setValue(this.selectedOptions, { emitEvent: false });
        } else {
            this.selectControl.setValue(this.selectedOptions.length > 0 ? this.selectedOptions[0] : null, {
                emitEvent: false,
            });
        }

        this.cdr.markForCheck();
    }

    private resolveOptionsFromValue(valuesToFind: any[]): void {
        const found: DropdownOption[] = [];
        for (const val of valuesToFind) {
            const option = (this.options || []).find((opt) => this.getValue(opt) === val);
            if (option) {
                found.push(option);
            }
        }

        if (this.multiple) {
            this.selectedOptions = found;
        } else {
            this.selectedOptions = found.length > 0 ? [found[0]] : [];
        }
    }

    public registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.propagateTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.selectControl.disable();
            this.searchControl.disable();
        } else {
            this.selectControl.enable();
            this.searchControl.enable();
        }
        this.cdr.markForCheck();
    }

    public get hasSelectedOptions(): boolean {
        return this.selectedOptions && this.selectedOptions.length > 0;
    }

    public get showSearchInput(): boolean {
        if (this.displayMode === 'dropdown') {
            // Always show input in dropdown mode
            return true;
        }
        // In chip mode, hide input when single selection is made
        return !this.hasSelectedOptions || this.multiple;
    }

    public get showChips(): boolean {
        // Show chips in chip mode, or when multiple mode is enabled
        return this.displayMode === 'chip' || this.multiple;
    }

    public shouldShowClearButton(): boolean {
        // Don't show clear button when there's a selection
        return false;
    }
}
